/**
 * Product builder selection defaults, indexes, and normalization helpers.
 * Load before files that read window.Express.productBuilderState.
 */
(() => {
  window.Express = window.Express || {};

  function createDefaultSelections(config) {
    return (config.groups || []).reduce((accumulator, group) => {
      const defaults = Array.isArray(group.defaultOptionKeys) ? group.defaultOptionKeys.slice() : [];
      const fallback = group.required && group.options[0] ? [group.options[0].key] : [];
      accumulator[group.groupTypeKey] = group.selectionMode === 'multiple'
        ? (defaults.length ? defaults : fallback)
        : (defaults.length ? defaults.slice(0, 1) : fallback.slice(0, 1));
      return accumulator;
    }, {});
  }

  function createBuilderIndexes(config) {
    const groups = config?.groups || [];
    const groupsByKey = new Map();
    const optionsByGroupKey = new Map();
    const optionIndexByGroupKey = new Map();

    groups.forEach((group, groupIndex) => {
      const optionsByKey = new Map();
      const optionIndexes = new Map();

      groupsByKey.set(group.groupTypeKey, { group, index: groupIndex });
      (group.options || []).forEach((option, optionIndex) => {
        optionsByKey.set(option.key, option);
        optionIndexes.set(option.key, optionIndex);
      });

      optionsByGroupKey.set(group.groupTypeKey, optionsByKey);
      optionIndexByGroupKey.set(group.groupTypeKey, optionIndexes);
    });

    return { groupsByKey, optionsByGroupKey, optionIndexByGroupKey };
  }

  function getIndexedGroup(indexes, groupKey) {
    return indexes?.groupsByKey?.get(groupKey)?.group || null;
  }

  function getIndexedGroupIndex(indexes, groupKey) {
    return indexes?.groupsByKey?.get(groupKey)?.index ?? -1;
  }

  function getIndexedOption(indexes, groupKey, optionKey) {
    return indexes?.optionsByGroupKey?.get(groupKey)?.get(optionKey) || null;
  }

  function getIndexedOptionIndex(indexes, groupKey, optionKey) {
    return indexes?.optionIndexByGroupKey?.get(groupKey)?.get(optionKey) ?? -1;
  }

  function collectSelectedOptions(groups, selections, indexes = null) {
    return groups.flatMap((group) =>
      (selections[group.groupTypeKey] || [])
        .map((optionKey) => {
          const option = getIndexedOption(indexes, group.groupTypeKey, optionKey)
            || (group.options || []).find((entry) => entry.key === optionKey);
          return option ? {
            ...option,
            groupKey: group.groupTypeKey,
            groupLabel: group.label,
            optionComponentSets: Array.isArray(group.optionComponentSets) ? group.optionComponentSets : [],
          } : null;
        })
        .filter(Boolean)
    );
  }

  function toggleOption(group, optionKey, selections) {
    const selected = selections[group.groupTypeKey] || [];
    selections[group.groupTypeKey] = group.selectionMode === 'multiple'
      ? (selected.includes(optionKey) ? selected.filter((entry) => entry !== optionKey) : selected.concat(optionKey))
      : (!group.required && selected.length === 1 && selected[0] === optionKey ? [] : [optionKey]);
  }

  function hasRequiredSelections(groups, selections) {
    return groups.every((group) => !group.required || (selections[group.groupTypeKey] || []).length > 0);
  }

  function normalizeSelections(config, input = {}, indexes = null) {
    const defaults = createDefaultSelections(config);

    return (config.groups || []).reduce((normalized, group) => {
      const rawSelection = Array.isArray(input[group.groupTypeKey])
        ? input[group.groupTypeKey]
        : (input[group.groupTypeKey] ? [input[group.groupTypeKey]] : []);
      const validSelection = rawSelection
        .map((entry) => String(entry))
        .filter((optionKey) => Boolean(getIndexedOption(indexes, group.groupTypeKey, optionKey)
          || (group.options || []).find((option) => option.key === optionKey)));

      normalized[group.groupTypeKey] = validSelection.length
        ? (group.selectionMode === 'multiple' ? validSelection : validSelection.slice(0, 1))
        : (defaults[group.groupTypeKey] || []);

      return normalized;
    }, {});
  }

  window.Express.productBuilderState = {
    createDefaultSelections,
    createBuilderIndexes,
    getIndexedGroup,
    getIndexedGroupIndex,
    getIndexedOption,
    getIndexedOptionIndex,
    collectSelectedOptions,
    toggleOption,
    hasRequiredSelections,
    normalizeSelections,
  };
})();
